import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { NotificationService } from '../../common/service/notification.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';

import * as mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import * as turf from '@turf/turf';

import { environment } from '../../../environments/environment';
import { HeaderActionsComponent } from '../../common/header-actions.component';
import { ensureMapboxWorker } from '../../common/mapbox-worker';
import { TranslationService } from '../../common/service/translation-service.service';

import type { Feature, FeatureCollection, GeoJsonProperties, Geometry, Polygon as GJPolygon } from 'geojson';
import { GeoParseResponse, GeoUploadService } from '../../common/service/geo-upload.service';
import { DrawValidationService } from './map/services/draw-validation.service';
import {
  addMapSearchControls,
  MAP_SEARCH_FALLBACK_TEXTS,
  MAP_SEARCH_TEXT_KEYS,
  MapSearchControlsHandle,
  MapSearchRenderedTexts,
} from './map/map-search-controls';
import { firstValueFrom } from 'rxjs';
import { InfoTextBoxComponent, wikiUrl } from '../../common/info-text-box.component';
import {
  GeoCheckFinding,
  hasTracesShapeFinding,
  outlineFeatures,
  pointFeatures,
  precisionFindingsToLocated,
  precisionLayerPaint,
  PRECISION_VIOLET,
  TRACES_AMBER,
  TracesFindingItem,
  tracesFindingsToLocated,
  tracesLayerPaint,
} from './map/traces-finding-layer';
import { TracesGeometryLayerController } from './map/traces-geometry-layer';
import { TracesGeoState, watchTracesPending } from './map/traces-pending-poll';

const SIZE_SOFT_LIMIT = 4_500_000;
const TOLERANCE_STEPS = [0.00002, 0.00005, 0.0001] as const;
const HIGH_QUALITY = false;
const ADD_CHUNK_SIZE = 250;
const INTERSECTION_AREA_EPS = 1e-8;
const CIRCLE_STEPS = 64;
const MAX_CIRCLE_HA = 4;
const DEFAULT_POINT_RADIUS_HA = MAX_CIRCLE_HA; // 4 ha fallback for imported points
const MIN_CIRCLE_HA = 0.01;
const HEAVY_FEATURE_LIMIT = 200;
const HEAVY_VERTEX_LIMIT = 12000;

const EMPTY_FC: FeatureCollection<Geometry, GeoJsonProperties> = {
  type: 'FeatureCollection',
  features: [],
};

// ---------- Utilities ----------
const raf = () => new Promise<void>((r) => requestAnimationFrame(() => r()));
const idle = (ms = 0) =>
  new Promise<void>((resolve) => {
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number })
      .requestIdleCallback;
    if (typeof ric === 'function') ric(() => resolve(), { timeout: ms || 200 });
    else setTimeout(resolve, ms || 0);
  });

function debounce<T extends (...args: any[]) => void>(fn: T, wait = 120) {
  let t: any;
  return (...args: Parameters<T>) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
}
function throttle<T extends (...args: any[]) => void>(fn: T, ms = 120) {
  let last = 0;
  let timer: any = null;
  let pending: Parameters<T> | null = null;
  const run = () => {
    last = Date.now();
    timer = null;
    const args = pending;
    pending = null;
    if (args) fn(...args);
  };
  return (...args: Parameters<T>) => {
    pending = args;
    const now = Date.now();
    if (!timer) {
      const wait = Math.max(0, ms - (now - last));
      timer = setTimeout(run, wait);
    }
  };
}

const DRAW_STYLES: any[] = [
  {
    id: 'gl-draw-polygon-fill-inactive',
    type: 'fill',
    filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'active', 'true']] as any,
    paint: { 'fill-color': '#00C853', 'fill-opacity': 0.25 },
  },
  {
    id: 'gl-draw-polygon-fill-active',
    type: 'fill',
    filter: ['all', ['==', '$type', 'Polygon'], ['==', 'active', 'true']] as any,
    paint: { 'fill-color': '#00E676', 'fill-opacity': 0.3 },
  },
  {
    id: 'gl-draw-polygon-stroke-inactive',
    type: 'line',
    filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'active', 'true']] as any,
    paint: { 'line-color': '#00E676', 'line-width': 3 },
  },
  {
    id: 'gl-draw-polygon-stroke-active',
    type: 'line',
    filter: ['all', ['==', '$type', 'Polygon'], ['==', 'active', 'true']] as any,
    paint: { 'line-color': '#00FF95', 'line-width': 4 },
  },

  {
    id: 'gl-draw-polygon-and-line-vertex-halo-active',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']] as any,
    paint: { 'circle-radius': 7, 'circle-color': '#ffffff' },
  },
  {
    id: 'gl-draw-polygon-and-line-vertex-active',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']] as any,
    paint: { 'circle-radius': 4, 'circle-color': '#00C853' },
  },
  {
    id: 'gl-draw-polygon-midpoint-halo',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']] as any,
    paint: { 'circle-radius': 6, 'circle-color': '#ffffff' },
  },
  {
    id: 'gl-draw-polygon-midpoint',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']] as any,
    paint: { 'circle-radius': 4, 'circle-color': '#d4d4d4' },
  },
  {
    id: 'gl-draw-polygon-and-line-vertex-active-selected',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'vertex'], ['==', 'active', 'true']] as any,
    paint: { 'circle-radius': 5, 'circle-color': '#2196F3', 'circle-stroke-width': 2, 'circle-stroke-color': '#fff' },
  },
];

// left-hand rule utils
function ensureClosed(ring: [number, number][]): [number, number][] {
  if (!ring?.length) return ring;
  const [fx, fy] = ring[0];
  const [lx, ly] = ring[ring.length - 1];
  return fx === lx && fy === ly ? ring : [...ring, [fx, fy]];
}
function signedArea(ring: [number, number][]): number {
  let sum = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[i + 1];
    sum += (x2 - x1) * (y2 + y1);
  }
  return -sum;
}
function isClockwise(ring: [number, number][]): boolean {
  return signedArea(ensureClosed(ring)) < 0;
}
function orientRing(ring: [number, number][], wantClockwise: boolean): [number, number][] {
  const r = ensureClosed(ring);
  const cw = isClockwise(r);
  return cw === wantClockwise ? r : [...r].reverse();
}
function enforceLeftHandRings(coords: [number, number][][]): [number, number][][] {
  if (!Array.isArray(coords) || !coords.length) return coords;
  const out: [number, number][][] = [];
  out.push(orientRing(coords[0], true)); // outer CW
  for (let i = 1; i < coords.length; i++) out.push(orientRing(coords[i], false)); // holes CCW
  return out;
}
function orientedFeature(f: Feature<GJPolygon>): Feature<GJPolygon> {
  const coords = f.geometry.coordinates ?? [];
  return {
    type: 'Feature',
    properties: { ...(f.properties ?? {}) },
    geometry: { type: 'Polygon', coordinates: enforceLeftHandRings(coords as any) },
  };
}

function normalizeForDraw(input: any): Feature<GJPolygon>[] {
  const feats: Feature[] = !input
    ? []
    : input.type === 'FeatureCollection'
      ? (input.features ?? [])
      : input.type === 'Feature'
        ? [input]
        : input.type
          ? ([{ type: 'Feature', properties: {}, geometry: input as Geometry }] as any)
          : [];

  const out: Feature<GJPolygon>[] = [];
  for (const f of feats) {
    const g = f?.geometry;
    if (!g) continue;
    if (g.type === 'Polygon') out.push(f as Feature<GJPolygon>);
    else if (g.type === 'MultiPolygon') {
      const rings = g.coordinates;
      for (let i = 0; i < rings.length; i++) {
        out.push({
          type: 'Feature',
          properties: { ...(f.properties ?? {}), _part: i },
          geometry: { type: 'Polygon', coordinates: rings[i] },
        } as any);
      }
    }
  }
  return out;
}

// ---------- NEW: full-GeoJSON coordinate validation ----------
type InvalidCoord = { path: string; lng: number; lat: number };

function forEachPosition(g: any, cb: (lng: number, lat: number, path: string) => void, path = 'root') {
  if (!g) return;
  const walk = (coords: any, pth: string) => {
    if (!coords) return;
    if (typeof coords[0] === 'number') {
      cb(coords[0], coords[1], pth);
      return;
    }
    for (let i = 0; i < coords.length; i++) walk(coords[i], `${pth}/${i}`);
  };
  if (g.type === 'FeatureCollection') {
    g.features?.forEach(
      (f: any, i: number) => f?.geometry && walk(f.geometry.coordinates, `${path}.features[${i}].geometry`)
    );
  } else if (g.type === 'Feature') {
    g.geometry && walk(g.geometry.coordinates, `${path}.geometry`);
  } else if (g.type) {
    walk(g.coordinates, path);
  }
}

function findInvalidCoord(geojson: any): InvalidCoord | null {
  let bad: InvalidCoord | null = null;
  forEachPosition(geojson, (lng, lat, path) => {
    if (bad) return;
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
      bad = { path, lng, lat };
    }
  });
  return bad;
}

// ===== Circle detection & ring utils =====

// Return ring without its repeated closing coordinate
function openRing(coords: [number, number][]): [number, number][] {
  if (!coords?.length) return [];
  const a = coords[0],
    b = coords[coords.length - 1];
  return a && b && a[0] === b[0] && a[1] === b[1] ? coords.slice(0, -1) : coords;
}

// Geometry-based "looks like circle?" check (stable even after re-import)
function looksLikeCircle(f: Feature<GJPolygon>, { minVertices = 16, cvTolerance = 0.02 } = {}): boolean {
  try {
    const ring = (f.geometry?.coordinates?.[0] ?? []) as [number, number][];
    const pts = openRing(ring);
    if (pts.length < minVertices) return false;

    const c = (turf.centroid(f as any) as any)?.geometry?.coordinates as [number, number];
    if (!c) return false;

    const radii = pts.map(([lng, lat]) => turf.distance([lng, lat], c, { units: 'meters' }) as number);
    const mean = radii.reduce((s, r) => s + r, 0) / radii.length;
    if (!Number.isFinite(mean) || mean <= 0) return false;
    const variance = radii.reduce((s, r) => s + (r - mean) * (r - mean), 0) / radii.length;
    const cv = Math.sqrt(Math.max(0, variance)) / mean;
    return cv <= cvTolerance;
  } catch {
    return false;
  }
}

function isCircleFeature(f: any): boolean {
  const p = (f?.properties ?? {}) as any;
  if (p?.sc_circle === true || p?.shape === 'circle' || p?.isCircle === true || p?._shape === 'circle') return true;
  try {
    return looksLikeCircle(f as Feature<GJPolygon>);
  } catch {
    return false;
  }
}

function parseFeatureCollection(json: string | null | undefined): FeatureCollection | undefined {
  if (!json) return undefined;
  try {
    const parsed = JSON.parse(json);
    return parsed?.type === 'FeatureCollection' ? parsed : undefined;
  } catch {
    return undefined;
  }
}

export interface MapSelectData {
  lat?: number;
  lng?: number;
  geojson?: FeatureCollection;
  /**
   * Anonymised TRACES rejection markers for this site (app#287, Task 8) — raw JSON of
   * `RejectionItem[]` (`CompanySite.tracesFinding`), and whether the stored geometry has moved
   * on since (`CompanySite.tracesFindingStale`), which dims the markers.
   */
  tracesFinding?: string | null;
  tracesFindingStale?: boolean;
  /**
   * Raw JSON of `CompanySite.geoCheckFindings` (F4) — only the TRACES-derivation codes
   * (`PRECISION_*`, `HOLES_UNSUPPORTED`, `REPAIR_SPLIT`) are rendered here; every other geo-service
   * code is app#315, out of scope. Hidden after the first draw edit — it describes the STORED
   * geometry only.
   */
  geoFindings?: string | null;
  tracesGeoJson?: string | null;
  tracesGeoPending?: boolean;
  reloadTracesGeo?: () => Promise<TracesGeoState | undefined>;
}
export interface MapSelectResult {
  center: { lat: number; lng: number } | null;
  point: mapboxgl.LngLatLike;
  polygons: [number, number][][];
  polygonAreasM2: number[];
  totalAreaM2: number; // NEW
  totalAreaHa: number; // NEW
  geojson: FeatureCollection;
}

@Component({
  selector: 'app-map-select-dialog',
  imports: [CommonModule, MatDialogModule, TranslateModule, HeaderActionsComponent, MatIconModule],
  templateUrl: './map-select-dialog.component.html',
  styleUrls: ['./map-select-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated,
})
export class MapSelectDialogComponent implements OnInit, OnDestroy {
  private readonly data = inject<MapSelectData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<MapSelectDialogComponent>, { optional: true });
  private readonly snack = inject(NotificationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);
  private readonly geoUpload = inject(GeoUploadService);
  private drawValidation = inject(DrawValidationService);

  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef<HTMLInputElement>;

  map!: mapboxgl.Map;
  marker!: mapboxgl.Marker;
  draw!: MapboxDraw;
  toolsCtrl!: MapToolsControl;

  polygonIds: string[] = [];
  polygonRings: [number, number][][] = [];
  polygonAreasM2: number[] = [];

  isDrawing = false;
  isEditing = false;
  isAddingCircle = false;
  isUploading = false;
  uploadProgress = 0;

  hasUnsavedChanges = false;
  private lastHeavyDatasetNotice: string | null = null;

  private animId: number | null = null;
  private coordsPopup: mapboxgl.Popup | null = null;
  private searchControls: MapSearchControlsHandle | null = null;

  // NOTE: Bind this in your header next to the title (requirement #2)
  hintText = null as string | null;
  private hintTextKey: string | null = null;
  private validationErrors: string[] = [];
  private polygonsWithErrors = new Set<string>();

  // IDs for error highlight sources/layers
  private readonly ERR_SRC_KINKS = 'err-kinks-src';
  private readonly ERR_LAYER_KINKS = 'err-kinks-layer';
  private readonly ERR_SRC_OVERLAPS = 'err-overlaps-src';
  private readonly ERR_LAYER_OVERLAPS_FILL = 'err-overlaps-fill';
  private readonly ERR_LAYER_OVERLAPS_LINE = 'err-overlaps-line';
  private readonly ERR_SRC_BADPOLYS = 'err-badpolys-src';
  private readonly ERR_LAYER_BADPOLYS_LINE = 'err-badpolys-line';
  private readonly ERR_LAYER_BADPOLYS_FILL = 'err-badpolys-fill';

  // TRACES rejection markers (app#287 Task 8) — amber, NOT subject to HEAVY_FEATURE_LIMIT,
  // NOT cleared by clearErrorHighlightData (they are not an editor finding).
  private readonly ERR_SRC_TRACES = 'err-traces-src';
  private readonly ERR_LAYER_TRACES_LINE = 'err-traces-line';
  private readonly ERR_LAYER_TRACES_POINT = 'err-traces-point';
  // TRACES-derivation markers (PRECISION_*, HOLES_UNSUPPORTED, REPAIR_SPLIT) — violet, also outside the heavy limit,
  // hidden after the first draw edit (they describe the STORED geometry only).
  private readonly ERR_SRC_PRECISION = 'err-precision-src';
  private readonly ERR_LAYER_PRECISION_LINE = 'err-precision-line';
  private readonly ERR_LAYER_PRECISION_POINT = 'err-precision-point';
  private precisionHiddenAfterEdit = false;
  private legendCtrl: MapLegendControl | null = null;
  private tracesGeometryLayer: TracesGeometryLayerController | null = null;
  private tracesGeo: FeatureCollection | undefined;
  private stopTracesPendingWatch: (() => void) | null = null;
  tracesGeoPending = false;
  tracesGeoVisible = false;
  private readonly translation = inject(TranslationService);
  private readonly translate = inject(TranslateService);

  openHelp = () => InfoTextBoxComponent.openHelp(`${wikiUrl}/adding-geodata`, this.translation.language);
  handleUploadClick() {
    try {
      this.fileInput?.nativeElement?.click();
    } catch (e) {
      console.error(e);
    }
  }

  onFileSelected(evt: Event) {
    const input = evt.target as HTMLInputElement;
    const files = input?.files;
    if (files?.length) {
      this.handleUpload(files);
    }
    if (input) input.value = '';
  }

  private showMsg(message: string) {
    try {
      this.snack.success(message);
    } catch (e) {
      console.error(e);
    }
  }
  private showErr(message: string, err?: unknown, durationMs = 4500) {
    console.error(message, err ?? '');
    try {
      this.snack.error(message, undefined, err);
    } catch (e) {
      console.error(e);
    }
  }

  // Hide Draw's vertex/midpoint points for any polygon that is a circle
  private updateCircleVertexVisibility() {
    if (!this.isStyleReady()) return;

    // collect ids of polygons that are circles
    const circleIds = this.getAllPolygons()
      .filter((f) => isCircleFeature(f))
      .map((f: any) => String(f.id))
      .filter(Boolean);

    const listExpr: any = ['literal', circleIds];

    // A point belongs to a circle if either its 'parent' or 'featureId' matches a circle polygon id.
    const belongsToCircle: any = ['any', ['in', ['get', 'parent'], listExpr], ['in', ['get', 'featureId'], listExpr]];

    // For vertices: show when NOT belonging to a circle
    const vertexFilter: any = ['all', ['==', '$type', 'Point'], ['==', 'meta', 'vertex'], ['!', belongsToCircle]];

    // For midpoints: same logic
    const midpointFilter: any = ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint'], ['!', belongsToCircle]];

    const set = (layerId: string, filter: any) => {
      try {
        if (this.map.getLayer(layerId)) this.map.setFilter(layerId, filter);
      } catch (e) {
        console.error(e);
      }
    };

    set('gl-draw-polygon-and-line-vertex-halo-active', vertexFilter);
    set('gl-draw-polygon-and-line-vertex-active', vertexFilter);
    set('gl-draw-polygon-midpoint-halo', midpointFilter);
    set('gl-draw-polygon-midpoint', midpointFilter);
  }

  // Mapbox helpers
  private isStyleReady(): boolean {
    try {
      return !!this.map?.isStyleLoaded();
    } catch {
      return false;
    }
  }
  private setGeoJSON(srcId: string, features: Feature[]) {
    const src = this.map.getSource(srcId) as mapboxgl.GeoJSONSource | undefined;
    if (!src || !this.isStyleReady()) return;
    const data: FeatureCollection = { type: 'FeatureCollection', features: features ?? [] } as any;
    try {
      src.setData(data as any);
    } catch {}
  }

  private getAllPolygons(): Feature<GJPolygon>[] {
    const fc = this.draw.getAll() as FeatureCollection<Geometry, GeoJsonProperties>;
    const feats = (fc.features || []) as Feature[];
    const out: Feature<GJPolygon>[] = [];
    for (let i = 0; i < feats.length; i++) {
      const f = feats[i];
      if (f?.geometry?.type === 'Polygon') out.push(f as Feature<GJPolygon>);
    }
    return out;
  }
  private getSelectedIds(): string[] {
    try {
      return (this.draw as any).getSelectedIds?.() ?? [];
    } catch {
      return [];
    }
  }

  private getPolygonList() {
    const polys = this.getAllPolygons();
    const selected = new Set(this.getSelectedIds());
    return polys.map((f, i) => {
      const id = (f as any).id as string;
      const customName = String((f.properties as any)?.name ?? '').trim();
      let area = 0;
      try {
        area = turf.area(f as any) as number;
      } catch {}
      return {
        id,
        label: `${customName || `Polygon ${i + 1}`} – ${(area / 10000).toFixed(2)} ha`,
        selected: selected.has(id),
        hasError: this.polygonsWithErrors.has(id),
      };
    });
  }

  private selectPolygonById(id: string) {
    try {
      const f = this.draw.get(id) as Feature<GJPolygon> | undefined;
      if (!f) return;

      if (isCircleFeature(f)) {
        (this.draw as any).changeMode('simple_select', { featureIds: [id] });
        this.isEditing = false;
        this.setHint('idle');
      } else {
        (this.draw as any).changeMode('direct_select', { featureId: id });
        this.isEditing = true;
        this.setHint('edit');
      }
      this.refreshCtrl();
      this.focusCanvas();
    } catch (err) {
      this.showErr('Could_not_select_polygon_tr', err);
    }
  }

  private zoomToPolygon(id: string) {
    try {
      const f = this.draw.get(id) as Feature<GJPolygon> | undefined;
      if (!f) return;
      const bb = turf.bbox(f as any) as [number, number, number, number];
      if (bb?.every(Number.isFinite)) {
        this.map.fitBounds(
          [
            [bb[0], bb[1]],
            [bb[2], bb[3]],
          ],
          { padding: 40 }
        );
      }
    } catch {}
  }

  // Row click: zoom + edit (NO coords)
  private focusZoomEdit(id: string) {
    this.selectPolygonById(id);
    this.zoomToPolygon(id);
    this.toolsCtrl?.scrollToPolygon(id);
  }
  // Icon button: zoom + edit + coords/radius
  private focusZoomEditAndShowCoords(id: string) {
    this.selectPolygonById(id);
    this.zoomToPolygon(id);
    this.toolsCtrl?.scrollToPolygon(id);
    this.openCoordsPopup();
  }

  // NEW: delete polygon by id (no focusing/selecting first)
  private deletePolygonById(id: string) {
    try {
      this.draw.delete([id]);
    } catch (err) {
      this.showErr('Could_not_delete_polygon_tr', err);
      return;
    }
    // Recompute without switching modes or focusing
    this.postEditRecompute();
  }

  // Throttled recompute after edits
  private postEditRecompute = () => {
    this.enforceLeftHandRuleAll();
    this.handleDrawChange();
    this.refreshCtrl();
    this.hasUnsavedChanges = true;
    this.validateAll(false);
    this.updateErrorHighlights();
  };
  private throttledRecompute = throttle(this.postEditRecompute, 120);

  private onDrawCreate = () => {
    this.hidePrecisionFindingsAfterEdit();
    this.throttledRecompute();
  };
  private onDrawUpdate = () => {
    this.hidePrecisionFindingsAfterEdit();
    this.updateCircleVertexVisibility();
  };
  private onDrawDelete = () => {
    this.hidePrecisionFindingsAfterEdit();
    this.throttledRecompute();
  };

  private onModeChange = (e: any) => {
    this.zone.run(() => {
      const mode = e?.mode as string | undefined;
      const enteringDraw = mode === 'draw_polygon';
      const inVertexEdit = mode === 'direct_select';
      this.isDrawing = !!enteringDraw;
      this.isEditing = !!inVertexEdit;

      try {
        this.map.getCanvas().style.cursor = enteringDraw ? 'crosshair' : '';
        if (enteringDraw) this.map.doubleClickZoom.disable();
        else this.map.doubleClickZoom.enable();
      } catch {}

      this.setHint(enteringDraw ? 'draw' : this.isEditing ? 'edit' : 'idle');
      this.refreshCtrl();
      this.cdr.markForCheck();
    });
  };

  private onSelectionChange = () => {
    try {
      const sel = this.getSelectedIds();
      if (!sel.length) {
        this.refreshCtrl();
        return;
      }
      const id = sel[0];
      const f = this.draw.get(id) as Feature<GJPolygon> | undefined;
      const mode = (this.draw as any).getMode?.();

      if (f && isCircleFeature(f)) {
        (this.draw as any).changeMode('simple_select', { featureIds: [id] });
        this.isEditing = false;
        this.setHint('idle');
      } else if (mode === 'simple_select') {
        (this.draw as any).changeMode('direct_select', { featureId: id });
        this.isEditing = true;
        this.setHint('edit');
      }
      this.refreshCtrl();
      this.toolsCtrl?.scrollToPolygon(id);
      this.cdr.markForCheck();
    } catch {}
  };

  private onCanvasKeydown = (ev: KeyboardEvent) => {
    if (ev.key === 'Escape' && (this.isDrawing || this.isEditing || this.isAddingCircle)) {
      try {
        (this.draw as any).changeMode('simple_select');
      } catch {}
      this.isEditing = false;
      this.isDrawing = false;
      if (this.isAddingCircle) {
        try {
          this.map.off('click', this.onMapClickAddCircle as any);
        } catch {}
      }
      this.isAddingCircle = false;
      this.map.getCanvas().style.cursor = '';
      this.setHint('idle');
      this.refreshCtrl();
      this.cdr.markForCheck();
      return;
    }
    if ((ev.key === 'Backspace' || ev.key === 'Delete') && this.isEditing) {
      ev.preventDefault();
      try {
        (this.draw as any).trash();
      } catch {}
    }
  };

  private focusCanvas() {
    try {
      (this.map?.getCanvas() as HTMLCanvasElement)?.focus();
    } catch {}
  }

  private updateHint(key: string, fallback: string) {
    this.hintTextKey = key;
    this.hintText = fallback;
    this.translation
      .loadFromBackend(key)
      .then((msg) => {
        if (this.hintTextKey !== key) return;
        this.hintText = typeof msg === 'string' && msg.length ? msg : fallback;
        this.cdr.markForCheck();
      })
      .catch(() => {
        if (this.hintTextKey !== key) return;
        this.hintText = fallback;
        this.cdr.markForCheck();
      });
  }

  // Hints now only update the string; show it in header (requirement #2)
  private setHint(mode: 'idle' | 'draw' | 'edit') {
    if (mode === 'draw') {
      this.updateHint(
        'Click_to_add_points_Double_click_to_finish_Esc_to_cancel_tr',
        'Click to add points • Double-click to finish • Esc to cancel'
      );
    } else if (mode === 'edit') {
      this.updateHint(
        'Click_a_vertex_midpoint_to_edit_Drag_vertices_Delete_to_remove_vertices_Esc_to_finish_tr',
        'Click a vertex/midpoint to edit • Drag vertices • Delete to remove vertices • Esc to finish'
      );
    } else if (this.polygonIds.length) {
      this.updateHint(
        'Click_a_polygon_in_the_list_to_edit_Click_Save_when_done_tr',
        'Click a polygon in the list to edit • Click Save when done'
      );
    } else {
      this.updateHint('Click_Draw_to_start_tr', 'Click Draw to start!');
    }
    this.cdr.markForCheck();
  }

  // ---------- NEW: prevent translating polygons (force direct_select on feature press) ----------
  private onMouseDownForceDirectSelect = (e: mapboxgl.MapMouseEvent) => {
    try {
      if (!this.map || !this.draw) return;

      // If user pressed on a Draw polygon, switch to direct_select for that feature
      const drawableLayers = ['gl-draw-polygon-fill-active', 'gl-draw-polygon-fill-inactive'].filter((id) =>
        Boolean(this.map?.getLayer(id))
      );
      if (!drawableLayers.length) return;
      const hits = this.map.queryRenderedFeatures(e.point, {
        layers: drawableLayers,
      });
      if (!hits.length) return;

      // Find a feature id that matches a Draw feature
      const hit = hits.find((h: any) => h.layer?.id?.startsWith('gl-draw-'));
      if (!hit) return;

      const featureIdRaw = (hit as any)['id'] ?? hit.properties?.['id'];
      const targetId = featureIdRaw as string | number | undefined;
      if (targetId == null) return;

      const f = this.draw.get(String(targetId)) as Feature<GJPolygon> | undefined;
      if ((f?.properties as any)?._circle) return; // do not enter direct_select for circle

      const mode = (this.draw as any).getMode?.();
      if (mode === 'simple_select') {
        (this.draw as any).changeMode('direct_select', { featureId: String(targetId) });
        this.isEditing = true;
        this.setHint('edit');
        // Let the map pan as usual; we just ensured the polygon itself won't translate.
      }
    } catch {}
  };

  /**
   * The geocoder renders these into its own DOM, so they have to be resolved up front rather
   * than swapped in later like the header hint. A missing key echoes itself back, so anything
   * that is not a real translation falls back to the English copy.
   */
  private async resolveSearchTexts(): Promise<Partial<MapSearchRenderedTexts>> {
    const names = ['placeholder', 'noResults', 'searchFailed'] as const;
    const entries = await Promise.all(
      names.map(async (name) => {
        const key = MAP_SEARCH_TEXT_KEYS[name];
        try {
          const msg = await this.translation.loadFromBackend(key);
          const usable = typeof msg === 'string' && msg.length > 0 && msg !== key;
          return [name, usable ? msg : MAP_SEARCH_FALLBACK_TEXTS[name]] as const;
        } catch {
          return [name, MAP_SEARCH_FALLBACK_TEXTS[name]] as const;
        }
      })
    );
    return Object.fromEntries(entries) as Partial<MapSearchRenderedTexts>;
  }

  async ngOnInit() {
    try {
      const startLng = this.data?.lng ?? 0;
      const startLat = this.data?.lat ?? 20;
      this.marker = new mapboxgl.Marker({ draggable: true }).setLngLat([startLng, startLat]);
      ensureMapboxWorker();
      this.map = new mapboxgl.Map({
        accessToken: environment.mapbox.accessToken,
        container: this.mapContainer.nativeElement,
        style: 'mapbox://styles/mapbox/satellite-streets-v12',
        zoom: 6,
        center: this.marker.getLngLat(),
        // Off (Mapbox's own default): cooperative gestures only exist to stop a map from
        // hijacking the scroll of the page behind it. This dialog is full-screen (100vw/vh,
        // CDK overlay blocks the body scroll), so there is nothing behind it to protect --
        // it would only force "hold CMD to zoom" on every wheel tick. The read-only
        // common/map-view-dialog.component.ts sets it false for the same reason.
        cooperativeGestures: false,
        dragRotate: true,
        dragPan: true,
      });

      this.map.on('load', async () => {
        try {
          this.map.resize();

          // Search + "my location", added before the NavigationControl so they sit above it.
          this.searchControls = addMapSearchControls(this.map, {
            accessToken: environment.mapbox.accessToken,
            center: { lat: this.data?.lat, lng: this.data?.lng },
            language: this.translation.language,
            texts: await this.resolveSearchTexts(),
            onError: (messageKey) => this.showErr(messageKey),
          });

          this.map.addControl(new mapboxgl.NavigationControl());

          this.setHint('idle');

          // ===== (1) Disable polygon move by overriding draw modes =====
          const baseModes: any = (MapboxDraw as any).modes || {};
          const NoDragDirectSelect = {
            ...baseModes.direct_select,
            dragFeature() {
              // no-op
            },
          };
          this.draw = new MapboxDraw({
            displayControlsDefault: false,
            controls: { polygon: false, trash: false },
            defaultMode: 'simple_select',
            styles: DRAW_STYLES,
            modes: {
              ...baseModes,
              direct_select: NoDragDirectSelect,
            },
            // NOTE: we keep default modes; we enforce direct_select ourselves on press/selection
          });
          this.map.addControl(this.draw, 'top-left');

          // Map tools control (list + draw + circle button)
          this.toolsCtrl = new MapToolsControl({
            startPolygon: () => this.startPolygon(),
            startCircle: () => this.startCirclePlacement(),
            clearPolygon: () => this.clearPolygon(),
            deletePolygonById: (id: string) => this.deletePolygonById(id),
            getState: () => ({
              isDrawing: this.isDrawing,
              isEditing: this.isEditing,
              hasPolygon: this.getAllPolygons().length > 0,
            }),
            getPolygonList: () => this.getPolygonList(),
            focusZoomEdit: (id: string) => this.focusZoomEdit(id),
            focusZoomEditAndShowCoords: (id: string) => this.focusZoomEditAndShowCoords(id),
          });
          this.map.addControl(this.toolsCtrl, 'top-left');

          this.drawValidation.attach(this.map, this.draw, {
            // optional, only if you want to force-refresh custom UI immediately after a fix
            onAfterFix: () => {
              try {
                this.refreshCtrl();
              } catch {}
            },
          });

          // draw events
          this.map.on('draw.create', this.onDrawCreate);
          this.map.on('draw.update', this.onDrawUpdate);
          this.map.on('draw.delete', this.onDrawDelete);
          this.map.on('draw.modechange', this.onModeChange);
          this.map.on('draw.selectionchange', this.onSelectionChange);

          this.updateCircleVertexVisibility();

          const canvas = this.map.getCanvas() as HTMLCanvasElement;
          canvas.setAttribute('tabindex', '0');
          canvas.addEventListener('keydown', this.onCanvasKeydown, { passive: true });

          // NEW: prevent polygon translation by forcing direct_select on press over a polygon
          this.map.on('mousedown', this.onMouseDownForceDirectSelect);

          this.initErrorHighlightLayers();
          this.initTracesGeometryLayer();
          this.renderExternalFindings();

          // CHANGED: validate incoming data.geojson before loading
          if (this.data?.geojson) {
            const bad = findInvalidCoord(this.data.geojson);
            if (bad) {
              this.showErr('Invalid_coordinates_in_initial_GeoJSON_tr');
            } else {
              await this.addGeoJsonToDraw(this.data.geojson);
              this.fitAllLater();
              this.updateErrorHighlights();
              this.drawValidation.recompute();
            }
          }

          this.marker.on('dragend', () => (this.hasUnsavedChanges = true));

          this.startStrokeAnimationWhenReady();
          this.refreshCtrl();
          this.cdr.markForCheck();
        } catch (err) {
          this.showErr('Map_failed_to_initialize_tr', err);
        }

        // Keep polygon list height same as map height
        try {
          const syncHeights = () => this.toolsCtrl?.syncListHeightToMap(this.map);
          this.map.on('resize', syncHeights);
          syncHeights();
        } catch {}
      });

      this.marker.addTo(this.map);
    } catch (err) {
      this.showErr('Map_setup_failed_tr', err);
    }
  }

  // ---------- Error highlighting ----------
  private initErrorHighlightLayers() {
    const addEmptySrc = (id: string) => {
      if (!this.map.getSource(id)) {
        this.map.addSource(id, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
      }
    };
    addEmptySrc(this.ERR_SRC_KINKS);
    addEmptySrc(this.ERR_SRC_OVERLAPS);
    addEmptySrc(this.ERR_SRC_BADPOLYS);

    if (!this.map.getLayer(this.ERR_LAYER_KINKS)) {
      this.map.addLayer({
        id: this.ERR_LAYER_KINKS,
        type: 'circle',
        source: this.ERR_SRC_KINKS,
        paint: {
          'circle-radius': 6,
          'circle-color': '#ff1744',
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#fff',
        },
      });
    }
    if (!this.map.getLayer(this.ERR_LAYER_OVERLAPS_FILL)) {
      this.map.addLayer({
        id: this.ERR_LAYER_OVERLAPS_FILL,
        type: 'fill',
        source: this.ERR_SRC_OVERLAPS,
        paint: { 'fill-color': '#ff1744', 'fill-opacity': 0.35 },
      });
    }
    if (!this.map.getLayer(this.ERR_LAYER_OVERLAPS_LINE)) {
      this.map.addLayer({
        id: this.ERR_LAYER_OVERLAPS_LINE,
        type: 'line',
        source: this.ERR_SRC_OVERLAPS,
        paint: { 'line-color': '#ff1744', 'line-width': 2.5 },
      });
    }
    if (!this.map.getLayer(this.ERR_LAYER_BADPOLYS_FILL)) {
      this.map.addLayer({
        id: this.ERR_LAYER_BADPOLYS_FILL,
        type: 'fill',
        source: this.ERR_SRC_BADPOLYS,
        paint: { 'fill-color': '#ff1744', 'fill-opacity': 0.5 },
      });
    }
    if (!this.map.getLayer(this.ERR_LAYER_BADPOLYS_LINE)) {
      this.map.addLayer({
        id: this.ERR_LAYER_BADPOLYS_LINE,
        type: 'line',
        source: this.ERR_SRC_BADPOLYS,
        paint: { 'line-color': '#c6002b', 'line-width': 2 },
      });
    }

    addEmptySrc(this.ERR_SRC_TRACES);
    addEmptySrc(this.ERR_SRC_PRECISION);

    const tracesPaint = tracesLayerPaint(this.data?.tracesFindingStale ?? false);
    if (!this.map.getLayer(this.ERR_LAYER_TRACES_LINE)) {
      this.map.addLayer({
        id: this.ERR_LAYER_TRACES_LINE,
        type: 'line',
        source: this.ERR_SRC_TRACES,
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: tracesPaint.line as any,
      });
    }
    if (!this.map.getLayer(this.ERR_LAYER_TRACES_POINT)) {
      this.map.addLayer({
        id: this.ERR_LAYER_TRACES_POINT,
        type: 'circle',
        source: this.ERR_SRC_TRACES,
        filter: ['==', ['geometry-type'], 'Point'],
        paint: tracesPaint.circle as any,
      });
    }

    const precisionPaint = precisionLayerPaint();
    if (!this.map.getLayer(this.ERR_LAYER_PRECISION_LINE)) {
      this.map.addLayer({
        id: this.ERR_LAYER_PRECISION_LINE,
        type: 'line',
        source: this.ERR_SRC_PRECISION,
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: precisionPaint.line as any,
      });
    }
    if (!this.map.getLayer(this.ERR_LAYER_PRECISION_POINT)) {
      this.map.addLayer({
        id: this.ERR_LAYER_PRECISION_POINT,
        type: 'circle',
        source: this.ERR_SRC_PRECISION,
        filter: ['==', ['geometry-type'], 'Point'],
        paint: precisionPaint.circle as any,
      });
    }

    for (const layerId of [this.ERR_LAYER_TRACES_POINT, this.ERR_LAYER_PRECISION_POINT]) {
      this.map.on('click', layerId, (e) => {
        const feature = e.features?.[0];
        if (feature?.geometry?.type === 'Point') {
          this.map.flyTo({
            center: feature.geometry.coordinates as [number, number],
            zoom: Math.max(this.map.getZoom(), 15),
          });
        }
      });
      this.map.on('mouseenter', layerId, () => (this.map.getCanvas().style.cursor = 'pointer'));
      this.map.on('mouseleave', layerId, () => (this.map.getCanvas().style.cursor = ''));
    }
  }

  private initTracesGeometryLayer(): void {
    this.tracesGeometryLayer = new TracesGeometryLayerController(this.map as any);
    this.applyTracesGeoState(this.data ?? {});
    if (this.tracesGeoPending) this.startTracesPendingWatch();
  }

  toggleTracesGeometry(): void {
    this.tracesGeoVisible = !this.tracesGeoVisible;
    this.tracesGeometryLayer?.setVisible(this.tracesGeoVisible);
    this.cdr.markForCheck();
  }

  private applyTracesGeoState({ tracesGeoJson, tracesGeoPending }: TracesGeoState): void {
    this.tracesGeoPending = !!tracesGeoPending;
    this.tracesGeo = parseFeatureCollection(tracesGeoJson);
    this.tracesGeometryLayer?.setData(this.tracesGeo);
    this.cdr.markForCheck();
  }

  private startTracesPendingWatch(): void {
    const reload = this.data?.reloadTracesGeo;
    if (!reload) return;
    this.stopTracesPendingWatch = watchTracesPending({
      fetchSite: reload,
      onUpdate: (state) => this.zone.run(() => this.applyTracesGeoState(state)),
    });
  }

  /**
   * Renders the site's TRACES rejection markers and precision findings (app#287 Task 8, F4).
   * Deliberately called OUTSIDE `updateErrorHighlights`/`getLargeDatasetReason` — a site with
   * 625 polygons that skips the editor's own kink/overlap check must still show where TRACES
   * rejected it. Reads `this.data.geojson` (the INPUT FeatureCollection, not `draw.getAll()`) so
   * `place`/`featureIndex` line up with what the server computed them against.
   */
  private renderExternalFindings(): void {
    const geo = this.data?.geojson;

    let tracesItems: TracesFindingItem[] = [];
    if (this.data?.tracesFinding) {
      try {
        const parsed: unknown = JSON.parse(this.data.tracesFinding);
        if (Array.isArray(parsed)) tracesItems = parsed as TracesFindingItem[];
      } catch {
        // malformed/legacy JSON — render nothing rather than throw
      }
    }
    const tracesLocated = tracesFindingsToLocated(tracesItems);
    this.setGeoJSON(this.ERR_SRC_TRACES, [...outlineFeatures(geo, tracesLocated), ...pointFeatures(tracesLocated)]);

    let geoFindings: GeoCheckFinding[] = [];
    if (this.data?.geoFindings) {
      try {
        const parsed: unknown = JSON.parse(this.data.geoFindings);
        if (Array.isArray(parsed)) geoFindings = parsed as GeoCheckFinding[];
      } catch {
        // malformed/legacy JSON — render nothing rather than throw
      }
    }
    const precisionLocated = precisionFindingsToLocated(geoFindings, this.tracesGeo);
    this.setGeoJSON(this.ERR_SRC_PRECISION, [
      ...outlineFeatures(geo, precisionLocated),
      ...pointFeatures(precisionLocated),
    ]);
    this.precisionHiddenAfterEdit = false;

    if (tracesItems.length || geoFindings.length) {
      this.legendCtrl = new MapLegendControl({
        traces: tracesItems.length > 0,
        precision: geoFindings.length > 0,
        shape: hasTracesShapeFinding(geoFindings),
        stale: this.data?.tracesFindingStale ?? false,
        translate: (key: string) => this.translate.instant(key),
      });
      this.map.addControl(this.legendCtrl, 'bottom-left');
    }

    const firstPoint = pointFeatures(tracesLocated)[0] ?? pointFeatures(precisionLocated)[0];
    if (firstPoint) {
      this.map.flyTo({
        center: firstPoint.geometry.coordinates as [number, number],
        zoom: Math.max(this.map.getZoom(), 14),
      });
    }
  }

  /**
   * PRECISION markers describe the STORED geometry only — the moment the user edits a polygon,
   * that description is stale. Hidden once, on the first draw.create/update/delete after load;
   * `err-traces-*` is unaffected, since a TRACES rejection stays valid until resubmitted.
   */
  private hidePrecisionFindingsAfterEdit(): void {
    if (this.precisionHiddenAfterEdit || !this.map) return;
    this.precisionHiddenAfterEdit = true;
    for (const layerId of [this.ERR_LAYER_PRECISION_LINE, this.ERR_LAYER_PRECISION_POINT]) {
      if (this.map.getLayer(layerId)) this.map.setLayoutProperty(layerId, 'visibility', 'none');
    }
  }

  private overlapAreaM2(a: turf.AllGeoJSON, b: turf.AllGeoJSON): number {
    try {
      if (!turf.booleanIntersects(a as any, b as any)) return 0;

      const overlaps = (turf as any).booleanOverlap ? (turf as any).booleanOverlap(a as any, b as any) : false;
      const containsAB = (turf as any).booleanContains ? (turf as any).booleanContains(a as any, b as any) : false;
      const containsBA = (turf as any).booleanContains ? (turf as any).booleanContains(b as any, a as any) : false;
      const withinAB = (turf as any).booleanWithin ? (turf as any).booleanWithin(a as any, b as any) : false;
      const withinBA = (turf as any).booleanWithin ? (turf as any).booleanWithin(b as any, a as any) : false;

      let inter: any = null;
      try {
        inter = turf.intersect(a as any, b as any);
      } catch {}
      if (inter) return Math.max(0, turf.area(inter as any));

      if (overlaps || containsAB || containsBA || withinAB || withinBA) {
        const areaA = turf.area(a as any);
        const areaB = turf.area(b as any);
        if (containsAB || withinBA) return areaB; // b inside a
        if (containsBA || withinAB) return areaA; // a inside b
        return 0;
      }

      return 0;
    } catch {
      return 0;
    }
  }

  private updateErrorHighlights() {
    if (!this.isStyleReady()) return;

    const polys = this.getAllPolygons();
    const skipReason = this.getLargeDatasetReason(polys);
    if (skipReason) {
      this.clearErrorHighlightData();
      this.maybeAnnounceLargeDataset(skipReason, false);
      return;
    }

    const kinkPoints: Feature[] = [];
    const overlapFeats: Feature[] = [];
    const badPolySet = new Set<number>();

    // self-intersections
    for (let i = 0; i < polys.length; i++) {
      try {
        const k = turf.kinks(polys[i] as any) as FeatureCollection;
        const feats = k?.features || [];
        if (feats.length) {
          badPolySet.add(i);
          for (let f = 0; f < feats.length; f++) {
            const g = feats[f].geometry?.type;
            if (g === 'Point' || g === 'MultiPoint') kinkPoints.push(feats[f] as Feature);
          }
        }
      } catch {}
    }

    // overlaps (partial + full)
    for (let i = 0; i < polys.length; i++) {
      for (let j = i + 1; j < polys.length; j++) {
        const area = this.overlapAreaM2(polys[i] as any, polys[j] as any);
        if (area > INTERSECTION_AREA_EPS) {
          let inter: Feature | null = null;
          try {
            inter = turf.intersect(polys[i] as any, polys[j] as any) as Feature | null;
          } catch {}
          if (!inter) {
            const ai = turf.area(polys[i] as any);
            const aj = turf.area(polys[j] as any);
            inter = (ai <= aj ? polys[i] : polys[j]) as unknown as Feature;
          }
          overlapFeats.push(inter as Feature);
          badPolySet.add(i);
          badPolySet.add(j);
        }
      }
    }

    // orientation problems
    for (let i = 0; i < polys.length; i++) {
      const coords = (polys[i].geometry.coordinates ?? []) as [number, number][][];
      if (!coords.length) continue;
      if (!isClockwise(coords[0])) badPolySet.add(i);
      for (let r = 1; r < coords.length; r++) if (isClockwise(coords[r])) badPolySet.add(i);
    }

    const badPolyFeats: Feature[] = Array.from(badPolySet).map((idx) => polys[idx]) as Feature[];
    this.polygonsWithErrors.clear();
    for (const idx of badPolySet) {
      const id = (polys[idx] as any)?.id;
      if (id != null) this.polygonsWithErrors.add(String(id));
    }

    this.setGeoJSON(this.ERR_SRC_KINKS, kinkPoints);
    this.setGeoJSON(this.ERR_SRC_OVERLAPS, overlapFeats);
    this.setGeoJSON(this.ERR_SRC_BADPOLYS, badPolyFeats);
    this.refreshCtrl();
  }

  private clearErrorHighlightData() {
    this.setGeoJSON(this.ERR_SRC_KINKS, []);
    this.setGeoJSON(this.ERR_SRC_OVERLAPS, []);
    this.setGeoJSON(this.ERR_SRC_BADPOLYS, []);
    this.polygonsWithErrors.clear();
    this.refreshCtrl();
  }

  private getPolygonStats(polys: Feature<GJPolygon>[]) {
    let vertexCount = 0;
    for (const poly of polys) {
      const coords = (poly.geometry?.coordinates ?? []) as [number, number][][];
      for (const ring of coords) vertexCount += ring?.length ?? 0;
    }
    return { featureCount: polys.length, vertexCount };
  }

  private getLargeDatasetReason(polys: Feature<GJPolygon>[]): string | null {
    const { featureCount, vertexCount } = this.getPolygonStats(polys);
    if (featureCount > HEAVY_FEATURE_LIMIT) {
      return `Dataset has ${featureCount} polygons; skipping automatic highlighting to keep the map responsive.`;
    }
    if (vertexCount > HEAVY_VERTEX_LIMIT) {
      return `Dataset has about ${vertexCount} vertices; skipping automatic highlighting to keep the map responsive.`;
    }
    return null;
  }

  private maybeAnnounceLargeDataset(reason: string | null, force = false) {
    if (!reason) {
      this.lastHeavyDatasetNotice = null;
      return;
    }
    if (force || reason !== this.lastHeavyDatasetNotice) {
      this.showMsg(reason);
      this.lastHeavyDatasetNotice = reason;
    }
  }

  startPolygon(): void {
    if (!this.draw) return;
    this.isDrawing = true;
    this.hasUnsavedChanges = true;
    try {
      this.map.getCanvas().style.cursor = 'crosshair';
      this.map.doubleClickZoom.disable();
      (this.draw as any).changeMode('draw_polygon');
    } catch (err) {
      this.showErr('Could_not_start_drawing_mode_tr', err);
    }
    this.setHint('draw');
    this.refreshCtrl();
    this.cdr.markForCheck();
    this.focusCanvas();
  }

  // ---------- NEW: Circle creation/edit helpers ----------

  private startCirclePlacement(): void {
    if (!this.map) return;
    this.isAddingCircle = true;
    this.updateHint(
      'Click_on_the_map_to_set_circle_center_Esc_to_cancel_tr',
      'Click on the map to set circle center • Esc to cancel'
    );
    try {
      this.map.getCanvas().style.cursor = 'crosshair';
    } catch {}
    this.cdr.markForCheck();
    this.focusCanvas();

    // Attach click temporarily
    this.map.on('click', this.onMapClickAddCircle as any);
  }

  private onMapClickAddCircle = (e: mapboxgl.MapMouseEvent) => {
    if (!this.isAddingCircle) return;
    // stop "add circle" mode for now; popup handles the rest
    this.isAddingCircle = false;
    try {
      this.map.off('click', this.onMapClickAddCircle as any);
      this.map.getCanvas().style.cursor = '';
    } catch {}

    const { lng, lat } = e.lngLat;
    // open editor prefilled with clicked center
    this.openCircleEditor({ lat, lng });
  };

  private haToRadiusKm(ha: number): number {
    const areaM2 = ha * 10000;
    const rMeters = Math.sqrt(areaM2 / Math.PI);
    return rMeters / 1000; // km
  }

  private buildCircleFeature(
    lng: number,
    lat: number,
    radiusHa: number,
    extraProps?: GeoJsonProperties
  ): Feature<GJPolygon> {
    // clamp to allowed range
    const rHa = Math.max(MIN_CIRCLE_HA, Math.min(radiusHa, MAX_CIRCLE_HA));

    let circle: Feature<GJPolygon> = turf.circle([lng, lat], this.haToRadiusKm(rHa), {
      steps: CIRCLE_STEPS,
      units: 'kilometers',
    }) as any;

    circle = orientedFeature(circle);
    (circle.properties as any) = {
      ...(extraProps ?? {}),
      ...(circle.properties ?? {}),
      _circle: true,
      shape: 'circle',
      centerLng: lng,
      centerLat: lat,
      radiusHa: rHa,
    };
    return circle;
  }

  private addCircleToDraw(lng: number, lat: number, radiusHa: number, name = ''): string | null {
    try {
      const feat = this.buildCircleFeature(lng, lat, radiusHa, { name });
      const newId = (this.draw as any).add(feat as any) as string | string[];
      const id = Array.isArray(newId) ? newId[0] : newId;
      // Select but keep in simple_select to avoid vertex edits
      try {
        (this.draw as any).changeMode('simple_select', { featureIds: [id] });
      } catch {}
      this.postEditRecompute();
      this.zoomToPolygon(id);
      return id;
    } catch (err) {
      this.showErr('Could_not_create_circle_tr', err);
      return null;
    }
  }

  private circleFromPointCoord(
    coords: any,
    props?: GeoJsonProperties,
    id?: string | number | null
  ): Feature<GJPolygon> | null {
    if (!Array.isArray(coords) || coords.length < 2) return null;
    const [lng, lat] = coords;
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;

    const readRadius = (value: unknown): number | null => {
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (typeof value === 'string') {
        const n = parseFloat(value);
        return Number.isFinite(n) ? n : null;
      }
      return null;
    };
    const propRadius =
      readRadius((props as any)?.radiusHa) ??
      readRadius((props as any)?.radius_ha) ??
      readRadius((props as any)?.radiusHA) ??
      readRadius((props as any)?.circleHa) ??
      readRadius((props as any)?.circle_ha);
    const radiusHa = propRadius ?? DEFAULT_POINT_RADIUS_HA;

    const circle = this.buildCircleFeature(lng, lat, radiusHa, props);
    if (id != null) (circle as any).id = String(id);
    return circle;
  }

  private convertPointFeatures(input: any): any {
    if (!input) return input;

    const convertFeature = (feature: any): Feature[] => {
      if (!feature) return [];
      if (!feature.geometry) return [feature];
      const geom = feature.geometry;
      if (geom.type === 'Point') {
        const circle = this.circleFromPointCoord(geom.coordinates, feature.properties, feature.id);
        return circle ? [circle] : [];
      }
      if (geom.type === 'MultiPoint') {
        const coords = Array.isArray(geom.coordinates) ? geom.coordinates : [];
        const out: Feature[] = [];
        coords.forEach((coord: any, idx: number) => {
          const circle = this.circleFromPointCoord(
            coord,
            feature.properties,
            feature.id != null ? `${feature.id}-${idx}` : undefined
          );
          if (circle) out.push(circle);
        });
        return out;
      }
      return [feature];
    };

    if (input.type === 'FeatureCollection') {
      const nextFeatures: Feature[] = [];
      (input.features ?? []).forEach((feat: any) => {
        const converted = convertFeature(feat);
        if (converted.length) nextFeatures.push(...converted);
      });
      return { ...input, features: nextFeatures };
    }

    if (input.type === 'Feature') {
      const converted = convertFeature(input);
      if (converted.length === 1) return converted[0];
      if (converted.length > 1) return { type: 'FeatureCollection', features: converted };
      return input;
    }

    if (input.type === 'Point') {
      const circle = this.circleFromPointCoord(input.coordinates);
      return circle ?? input;
    }

    if (input.type === 'MultiPoint') {
      const coords = Array.isArray(input.coordinates) ? input.coordinates : [];
      const feats: Feature[] = [];
      coords.forEach((coord: any, idx: number) => {
        const circle = this.circleFromPointCoord(coord, undefined, idx);
        if (circle) feats.push(circle);
      });
      if (feats.length === 1) return feats[0];
      if (feats.length > 1) return { type: 'FeatureCollection', features: feats };
      return input;
    }

    return input;
  }

  private replacePolygonWith(id: string, newRing: [number, number][], newProps?: Record<string, any>): string | null {
    try {
      const f = this.draw.get(id) as any;
      if (!f) return null;
      const closed = ensureClosed(newRing);
      if (closed.length < 4) return null;

      const orientedOuter = orientRing(closed, true);
      const holes = (f.geometry?.coordinates?.slice(1) ?? []) as [number, number][][];

      this.draw.delete(id);
      const newF: Feature<GJPolygon> = {
        type: 'Feature',
        properties: { ...(f.properties ?? {}), ...(newProps ?? {}) },
        geometry: { type: 'Polygon', coordinates: [orientedOuter, ...holes.map((h) => orientRing(h, false))] },
      };
      const newId = (this.draw as any).add(newF) as string | string[];
      const nextId = Array.isArray(newId) ? newId[0] : newId;
      try {
        // keep simple_select for circle; direct_select for normal
        if ((newF.properties as any)?._circle) {
          (this.draw as any).changeMode('simple_select', { featureIds: [nextId] });
        } else {
          (this.draw as any).changeMode('direct_select', { featureId: nextId });
        }
      } catch {}
      this.postEditRecompute();
      return nextId;
    } catch (err) {
      this.showErr('Could_not_update_the_polygon_tr', err);
      return null;
    }
  }

  clearPolygon(): void {
    if (!this.draw) return;
    const sel = this.getSelectedIds();
    if (sel.length) {
      try {
        this.draw.delete(sel);
      } catch (err) {
        this.showErr('Could_not_delete_selected_polygon_tr', err);
      }
    }
    this.postEditRecompute();
  }

  private refreshCtrl() {
    try {
      this.toolsCtrl?.refresh();
    } catch {}
  }

  private handleDrawChange(): void {
    const polys = this.getAllPolygons();
    if (!polys.length) {
      this.resetPolygonState();
      this.cdr.markForCheck();
      return;
    }
    this.polygonIds = polys.map((f) => (f as any).id as string);
    this.polygonRings = polys.map((f: any) => (f.geometry.coordinates?.[0] ?? []) as [number, number][]);
    this.polygonAreasM2 = polys.map((p) => {
      try {
        return turf.area(p as any);
      } catch {
        return 0;
      }
    });
    this.cdr.markForCheck();
  }

  private resetPolygonState(): void {
    this.polygonIds = [];
    this.polygonRings = [];
    this.polygonAreasM2 = [];
    this.isDrawing = false;
    this.isEditing = false;
    this.isAddingCircle = false;
  }

  private enforceLeftHandRuleAll(): void {
    const polys = this.getAllPolygons();
    if (!polys.length) return;
    const updates: Array<{ id: string; coords: [number, number][][] }> = [];
    for (const f of polys) {
      const id = (f as any).id as string;
      const before = (f.geometry.coordinates ?? []) as [number, number][][];
      const oriented = enforceLeftHandRings(before);
      const changed =
        before.length !== oriented.length ||
        before.some(
          (r, i) =>
            r.length !== oriented[i].length ||
            r.some((p, j) => p[0] !== oriented[i][j][0] || p[1] !== oriented[i][j][1])
        );
      if (changed) updates.push({ id, coords: oriented });
    }
    for (const u of updates) {
      const old = this.draw.get(u.id) as Feature<GJPolygon> | undefined;
      if (!old) continue;
      try {
        this.draw.delete(u.id);
        const newF: Feature<GJPolygon> = {
          type: 'Feature',
          properties: { ...(old.properties ?? {}) },
          geometry: { type: 'Polygon', coordinates: u.coords },
        };
        const newId = (this.draw as any).add(newF) as string | string[];
        const nextId = Array.isArray(newId) ? newId[0] : newId;
        try {
          (this.draw as any).changeMode('direct_select', { featureId: nextId });
        } catch {}
      } catch (err) {
        this.showErr('Failed_to_enforce_ring_orientation_on_a_polygon_tr', err);
      }
    }
  }

  private startStrokeAnimationWhenReady() {
    const layerId = 'gl-draw-polygon-stroke-active';
    const tryStart = () => {
      if (!this.isStyleReady() || !this.map.getLayer(layerId)) {
        this.map.once('idle', tryStart);
        return;
      }
      this.zone.runOutsideAngular(() => {
        let phase = 0;
        const animate = () => {
          if (!this.isStyleReady() || !this.map.getLayer(layerId)) return;
          phase = (phase + 0.05) % 6;
          try {
            const on = Math.floor(phase) % 2 === 0;
            this.map.setPaintProperty(layerId, 'line-dasharray', on ? [2, 2] : [1, 3]);
          } catch {}
          this.animId = requestAnimationFrame(animate);
        };
        this.animId = requestAnimationFrame(animate);
      });
    };
    this.map.once('idle', tryStart);
  }

  downloadAllPolygonsAsGeoJSON(): void {
    if (!this.draw) return;
    try {
      const all = (this.draw.getAll() as FeatureCollection<Geometry, GeoJsonProperties>) ?? EMPTY_FC;
      const features = (all.features || []).filter((f) => f?.geometry?.type === 'Polygon');
      if (!features.length) return this.showMsg('No_polygons_found_to_export_tr');
      const fc: FeatureCollection = { type: 'FeatureCollection', features } as any;
      const blob = new Blob([JSON.stringify(fc, null, 2)], { type: 'application/geo+json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      a.href = url;
      a.download = `polygons-${ts}.geojson`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      a.remove();
      this.showMsg('GeoJSON_downloaded_tr');
    } catch (err) {
      this.showErr('Could_not_export_polygons_tr', err);
    }
  }

  private async handleUpload(files: FileList) {
    const file = files?.[0];
    if (!file) return;
    const name = (file.name || '').toLowerCase();
    const allowed = /\.geojson$|\.json$|\.zip$|\.csv$|\.xls$|\.xlsx$|\.qgz$|\.gpx$/.test(name);
    if (!allowed) return this.showMsg('Unsupported_map_files_tr');

    try {
      this.isUploading = true;
      this.uploadProgress = 0;
      this.hintText = 'Uploading… 0%';
      this.cdr.markForCheck();
      const resp: GeoParseResponse = await firstValueFrom(
        this.geoUpload.parseFileToGeoJSON(file, (pct) => {
          const progress = Math.max(0, Math.min(100, Math.round(pct)));
          this.uploadProgress = progress;
          this.hintText = `Uploading… ${progress}%`;
          this.cdr.markForCheck();
        })
      );
      if (resp?.success === false || resp?.error) {
        return this.showErr(resp.error ?? 'Server_returned_an_error_processing_the_file_tr');
      }
      if (!resp?.geojson) return this.showErr('Server_did_not_return_parsed_GeoJSON_tr');

      // NEW: upfront coordinate validation (reject bad files and do NOT load on map)
      const bad = findInvalidCoord(resp.geojson);
      if (bad) {
        this.showErr(
          `${await this.translation.loadFromBackend('Invalid_coordinates_in_file_Offender_at_tr')} '${bad.path}: lat=${bad.lat}, lng=${bad.lng}'`
        );
        return; // do not load
      }

      await this.addGeoJsonToDraw(resp.geojson);
      this.enforceLeftHandRuleAll();
      this.handleDrawChange();
      this.validateAll(true);
      this.fitAllLater();
      this.hasUnsavedChanges = true;
      this.updateErrorHighlights();
      this.showMsg('File_parsed_on_server_and_loaded_for_editing_tr');
      this.map.once('idle', () => this.drawValidation?.recompute());
    } catch (err) {
      this.showErr('Upload_or_server_parsing_failed_tr', err);
    } finally {
      this.isUploading = false;
      this.uploadProgress = 0;
      this.hintText = '';
      this.cdr.markForCheck();
    }
  }

  private async addGeoJsonToDraw(input: any) {
    try {
      const prepared = this.convertPointFeatures(input);
      const feats = normalizeForDraw(prepared);
      if (!feats.length) return this.showMsg('No_polygon_features_found_in_the_file_tr');

      const orientedFeats = feats.map(orientedFeature);
      if (orientedFeats.length <= ADD_CHUNK_SIZE) {
        try {
          (this.draw as any).add({ type: 'FeatureCollection', features: orientedFeats } as any);
        } catch {
          for (const feat of orientedFeats) {
            try {
              (this.draw as any).add(feat as any);
            } catch {}
          }
        }
      } else {
        for (let i = 0; i < orientedFeats.length; i += ADD_CHUNK_SIZE) {
          const chunk = orientedFeats.slice(i, i + ADD_CHUNK_SIZE);
          try {
            (this.draw as any).add({ type: 'FeatureCollection', features: chunk } as any);
          } catch {
            for (const feat of chunk) {
              try {
                (this.draw as any).add(feat as any);
              } catch {}
            }
          }
          await raf();
        }
      }
      this.handleDrawChange();
      this.refreshCtrl();
      this.cdr.markForCheck();
      this.hasUnsavedChanges = true;
      this.updateErrorHighlights();
    } catch (err) {
      this.showErr('Invalid_GeoJSON_Could_not_display_on_map_tr', err);
    }
  }

  private fitAllLater = debounce(async () => {
    try {
      await idle(50);
      const gj = this.draw.getAll() as FeatureCollection;
      const bb = turf.bbox(gj as any) as [number, number, number, number];
      if (bb?.every(Number.isFinite)) {
        this.map.fitBounds(
          [
            [bb[0], bb[1]],
            [bb[2], bb[3]],
          ],
          { padding: 40 }
        );
      }
    } catch (err) {
      // Defensive toast in case something slips through; normal path is caught by upfront validation.
      this.showErr('fitAllLater_failed_due_to_invalid_coordinates_Please_fix_your_data_and_try_again_tr', err); 
      console.warn('fitAllLater failed:', err);
    }
  }, 120);

  private countPositions(g: any): number {
    let n = 0;
    const walk = (coords: any) => {
      if (!coords) return;
      if (typeof coords[0] === 'number') {
        n += 1;
        return;
      }
      for (const c of coords) walk(c);
    };
    const each = (f: any) => f?.geometry && walk(f.geometry.coordinates);
    if (g?.type === 'FeatureCollection') g.features?.forEach(each);
    else if (g?.type === 'Feature') each(g);
    else if (g?.type) walk(g.coordinates);
    return n;
  }

  private trySimplifyIfLarge(fc: FeatureCollection) {
    const jsonLen = JSON.stringify(fc).length;
    const beforePts = this.countPositions(fc);
    if (jsonLen <= SIZE_SOFT_LIMIT) {
      return {
        result: fc,
        simplified: false,
        beforePts,
        afterPts: beforePts,
        tolUsed: undefined as number | undefined,
      };
    }
    let current: any = fc;
    let used: number | undefined;
    for (const tol of TOLERANCE_STEPS) {
      try {
        const simplified = turf.simplify(current as any, { tolerance: tol, highQuality: HIGH_QUALITY });
        const len = JSON.stringify(simplified).length;
        used = tol;
        current = simplified;
        if (len <= SIZE_SOFT_LIMIT) break;
      } catch {}
    }
    const afterPts = this.countPositions(current);
    return { result: current as FeatureCollection, simplified: true, beforePts, afterPts, tolUsed: used };
  }

  private async validateAll(notify = false): Promise<boolean> {
    const polys = this.getAllPolygons();
    const skipReason = this.getLargeDatasetReason(polys);
    if (skipReason) {
      this.validationErrors = [];
      this.maybeAnnounceLargeDataset(skipReason, notify);
      return true;
    }
    this.maybeAnnounceLargeDataset(null);
    const errors: string[] = [];

    // self-intersections
    for (let i = 0; i < polys.length; i++) {
      try {
        const k = turf.kinks(polys[i] as any) as any;
        const count = k?.features?.length ?? 0;
        if (count > 0) errors.push(`Polygon ${i + 1} has ${count} self-intersection(s).`);
      } catch {}
    }

    // overlaps
    for (let i = 0; i < polys.length; i++) {
      for (let j = i + 1; j < polys.length; j++) {
        const a = this.overlapAreaM2(polys[i] as any, polys[j] as any);
        if (a > INTERSECTION_AREA_EPS)
          errors.push(`Polygons ${i + 1} and ${j + 1} overlap (area ~ ${a.toFixed(2)} m²).`);
      }
    }

    // orientation
    for (let i = 0; i < polys.length; i++) {
      const coords = (polys[i].geometry.coordinates ?? []) as [number, number][][];
      if (!coords.length) continue;
      if (!isClockwise(coords[0])) errors.push(`Polygon ${i + 1} outer ring is not clockwise (left-hand rule).`);
      for (let r = 1; r < coords.length; r++)
        if (isClockwise(coords[r]))
          errors.push(`Polygon ${i + 1} hole #${r} is not counter-clockwise (left-hand rule).`);
    }

    this.validationErrors = errors;

    if (notify && errors.length)
      this.showErr(`${await this.translation.loadFromBackend('Validation_failed_tr')} '\n• ${errors.join('\n•')}'`);
    else if (notify && !errors.length) this.showMsg('Polygons_validated_OK_and_Saved_tr');

    return errors.length === 0;
  }

  async saveAndClose(): Promise<void> {
    try {
      this.enforceLeftHandRuleAll();
      this.handleDrawChange();
      const ok = this.validateAll(true);
      this.updateErrorHighlights();
      if (!ok) return;

      const drawGeoJson = (this.draw?.getAll() as FeatureCollection<Geometry, GeoJsonProperties>) ?? EMPTY_FC;
      const polygonFeatures = (drawGeoJson.features || []).filter((f) => f?.geometry?.type === 'Polygon');

      for (const f of polygonFeatures) {
        const circle = isCircleFeature(f as any);
        (f.properties as any) = {
          ...(f.properties ?? {}),
          _circle: circle,
          shape: circle ? 'circle' : 'polygon',
        };
      }

      let center: { lat: number; lng: number } | null = null;
      if (polygonFeatures.length) {
        try {
          const fc: FeatureCollection = { type: 'FeatureCollection', features: polygonFeatures as any };
          const c = (turf.centroid(fc) as any)?.geometry?.coordinates as [number, number] | undefined;
          if (c && Number.isFinite(c[0]) && Number.isFinite(c[1])) center = { lat: c[1], lng: c[0] };
        } catch {}
      }
      if (!center) {
        const p = this.marker.getLngLat();
        center = { lat: p.lat, lng: p.lng };
      }

      // NEW: compute total area across all shapes (circles included, since they are polygons)
      const totalAreaM2 = polygonFeatures.reduce((sum, f) => {
        try {
          return sum + (turf.area(f as any) || 0);
        } catch {
          return sum;
        }
      }, 0);
      const totalAreaHa = Math.round((totalAreaM2 / 10000) * 100) / 100;

      console.log('Area Hectares: ', totalAreaHa);

      let toSave: FeatureCollection = drawGeoJson as FeatureCollection;
      const { result, simplified, beforePts, afterPts, tolUsed } = this.trySimplifyIfLarge(toSave);
      toSave = result;
      if (simplified)
        this.showMsg(`Large dataset simplified (${beforePts}→${afterPts} pts${tolUsed ? `, tol=${tolUsed}` : ''}).`);

      this.hasUnsavedChanges = false;
      this.dialogRef?.close(<MapSelectResult>{
        center,
        point: this.marker.getLngLat(),
        polygons: this.polygonRings,
        polygonAreasM2: this.polygonAreasM2,
        totalAreaM2, // NEW
        totalAreaHa, // NEW
        geojson: toSave,
      });
    } catch (err) {
      this.showErr('Save_failed_Dataset_may_be_too_large_or_invalid_tr', err);
    }
  }

  private getActivePolygon(): { id: string; feature: Feature<GJPolygon>; ring: [number, number][] } | null {
    const sel = this.getSelectedIds();
    const polys = this.getAllPolygons();
    const target = sel.length ? polys.find((p) => (p as any).id === sel[0]) : polys[0];
    if (!target) return null;
    const id = (target as any).id as string;
    const raw = (target.geometry.coordinates?.[0] ?? []) as any[];
    const ring: [number, number][] = raw.map((p: any) => [p[0], p[1]] as [number, number]);
    return { id, feature: target, ring };
  }

  private replacePolygon(id: string, newRing: [number, number][]): string | null {
    try {
      const f = this.draw.get(id) as any;
      if (!f) return null;
      const closed = ensureClosed(newRing);
      if (closed.length < 4) return null;
      const orientedOuter = orientRing(closed, true);
      const holes = (f.geometry?.coordinates?.slice(1) ?? []) as [number, number][][];

      this.draw.delete(id);
      const newF: Feature<GJPolygon> = {
        type: 'Feature',
        properties: { ...(f.properties ?? {}) },
        geometry: { type: 'Polygon', coordinates: [orientedOuter, ...holes.map((h) => orientRing(h, false))] },
      };
      const newId = (this.draw as any).add(newF) as string | string[];
      const nextId = Array.isArray(newId) ? newId[0] : newId;
      try {
        (this.draw as any).changeMode('direct_select', { featureId: nextId });
      } catch {}
      this.postEditRecompute();
      return nextId;
    } catch (err) {
      this.showErr('Update_polygon_failed_tr', err);
      return null;
    }
  }

  private openCircleEditor(init: { id?: string; lat: number; lng: number; radiusHa?: number }) {
    if (this.coordsPopup) {
      this.coordsPopup.remove();
      this.coordsPopup = null;
    }

    let at = new mapboxgl.LngLat(init.lng, init.lat);
    if (init.id) {
      try {
        const f = this.draw.get(init.id) as Feature<GJPolygon> | undefined;
        const center = (turf.centroid(f as any) as any)?.geometry?.coordinates as [number, number];
        if (center?.every(Number.isFinite)) at = new mapboxgl.LngLat(center[0], center[1]);
      } catch {}
    }

    const wrap = document.createElement('div');
    wrap.className = 'coords-popup circle-popup';
    wrap.style.minWidth = '360px';

    const title = document.createElement('div');
    title.className = 'coords-popup__h';
    title.textContent = init.id ? 'Edit circle' : 'Create circle';
    wrap.appendChild(title);

    const nameLabel = document.createElement('label');
    nameLabel.textContent = this.translate.instant('Polygon_name_tr');
    nameLabel.style.display = 'block';
    nameLabel.style.fontSize = '12px';
    nameLabel.style.marginBottom = '4px';
    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.value = init.id ? String((this.draw.get(init.id) as any)?.properties?.name ?? '').trim() : '';
    nameInput.placeholder = this.translate.instant('Polygon_name_tr');
    nameInput.maxLength = 120;
    nameInput.style.width = '100%';
    nameInput.style.boxSizing = 'border-box';
    nameInput.style.padding = '6px';
    nameInput.style.marginBottom = '8px';
    wrap.appendChild(nameLabel);
    wrap.appendChild(nameInput);

    const form = document.createElement('div');
    form.style.display = 'grid';
    form.style.gridTemplateColumns = '1fr 1fr';
    form.style.gap = '8px';

    const mkInput = (label: string, value: string, step = '0.000001') => {
      const wrap = document.createElement('div');
      wrap.style.display = 'flex';
      wrap.style.flexDirection = 'column';
      const l = document.createElement('label');
      l.textContent = label;
      l.style.fontSize = '12px';
      const inp = document.createElement('input');
      inp.type = 'number';
      inp.step = step;
      inp.value = value;
      inp.style.padding = '6px';
      wrap.appendChild(l);
      wrap.appendChild(inp);
      return { wrap, input: inp };
    };

    const latEl = mkInput('Latitude', String(init.lat));
    const lngEl = mkInput('Longitude', String(init.lng));
    const radEl = mkInput('Radius (ha)', String(init.radiusHa ?? 4), '0.01');
    radEl.input.min = String(MIN_CIRCLE_HA);
    radEl.input.max = String(MAX_CIRCLE_HA);

    form.appendChild(latEl.wrap);
    form.appendChild(lngEl.wrap);
    // full width for radius
    const radiusWrap = document.createElement('div');
    radiusWrap.style.gridColumn = '1 / span 2';
    radiusWrap.appendChild(radEl.wrap);
    form.appendChild(radiusWrap);

    const btnRow = document.createElement('div');
    btnRow.style.display = 'flex';
    btnRow.style.gap = '8px';
    btnRow.style.marginTop = '10px';

    const saveBtn = document.createElement('button');
    saveBtn.type = 'button';
    saveBtn.textContent = 'Save';
    saveBtn.className = 'coords-popup__btn';

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.textContent = 'Cancel';
    cancelBtn.className = 'coords-popup__btn';

    btnRow.appendChild(saveBtn);
    btnRow.appendChild(cancelBtn);

    wrap.appendChild(form);
    wrap.appendChild(btnRow);

    cancelBtn.addEventListener('click', () => {
      try {
        this.coordsPopup?.remove();
      } finally {
        this.coordsPopup = null;
      }
    });

    saveBtn.addEventListener('click', () => {
      const lat = parseFloat(latEl.input.value);
      const lng = parseFloat(lngEl.input.value);
      let radiusHa = parseFloat(radEl.input.value);
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(radiusHa) || radiusHa <= 0) {
        this.showErr('Please_enter_valid_numeric_lat_lng_tr');
        return;
      }
      if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
        this.showErr('Lat_must_be_between_90_and_Lng_between_180_tr');
        return;
      }
      if (radiusHa > MAX_CIRCLE_HA) {
        radiusHa = MAX_CIRCLE_HA;
        this.showMsg('Circle_radius_capped_at_4_ha_tr');
      }
      if (radiusHa < MIN_CIRCLE_HA) {
        radiusHa = MIN_CIRCLE_HA;
        this.showMsg('Circle_radius_raised_to_minimum_ha_tr');
      }

      const circle = this.buildCircleFeature(lng, lat, radiusHa);
      const ring = (circle.geometry.coordinates?.[0] ?? []) as [number, number][];

      if (init.id) {
        const nextId = this.replacePolygonWith(init.id, ring, {
          _circle: true,
          shape: 'circle',
          centerLng: lng,
          centerLat: lat,
          radiusHa,
          name: nameInput.value.trim(),
        });
        if (nextId) {
          this.showMsg('Circle_updated_tr');
          this.coordsPopup?.remove();
          this.coordsPopup = null;
          this.zoomToPolygon(nextId);
        }
      } else {
        const id = this.addCircleToDraw(lng, lat, radiusHa, nameInput.value.trim());
        if (id) {
          this.showMsg('Circle_added_tr');
          this.coordsPopup?.remove();
          this.coordsPopup = null;
          this.zoomToPolygon(id);
        }
      }
    });

    try {
      this.coordsPopup = new mapboxgl.Popup({ closeButton: true, closeOnClick: false, offset: 12, maxWidth: '420px' })
        .setLngLat(at)
        .setDOMContent(wrap)
        .addTo(this.map);
    } catch (err) {
      this.showErr('Could_not_open_circle_editor_tr', err);
    }
  }

  private openCoordsPopup() {
    if (this.coordsPopup) {
      this.coordsPopup.remove();
      this.coordsPopup = null;
    }
    const active = this.getActivePolygon();
    if (!active) return;

    // If this is a circle, open the circle editor instead of vertex table
    const props = (active.feature.properties ?? {}) as any;
    if (props._circle) {
      const rHa = Number.isFinite(props.radiusHa)
        ? Number(props.radiusHa)
        : Math.max(0.01, turf.area(active.feature as any) / 10000);
      const lat = Number.isFinite(props.centerLat) ? Number(props.centerLat) : (active.ring[0]?.[1] ?? 0);
      const lng = Number.isFinite(props.centerLng) ? Number(props.centerLng) : (active.ring[0]?.[0] ?? 0);
      this.openCircleEditor({ id: active.id, lat, lng, radiusHa: rHa });
      return;
    }

    // Limit to 20 vertices (requirement #3)
    const fullRing = active.ring;
    const openRing: [number, number][] = fullRing.length > 1 ? fullRing.slice(0, fullRing.length - 1) : [...fullRing];
    if (openRing.length > 20) {
      this.showErr('Unable_to_edit_your_polygon_has_more_than_20_data_points_tr');
      return;
    }

    let at = this.marker.getLngLat();
    try {
      const center = (turf.centroid(active.feature) as any)?.geometry?.coordinates as [number, number];
      if (center?.every(Number.isFinite)) at = new mapboxgl.LngLat(center[0], center[1]);
    } catch {}

    const wrap = document.createElement('div');
    wrap.className = 'coords-popup coords-popup--table';
    wrap.style.minWidth = '400px';
    wrap.style.maxHeight = '295px';

    const title = document.createElement('div');
    title.className = 'coords-popup__h';
    title.textContent = 'Edit vertices';
    wrap.appendChild(title);

    const nameLabel = document.createElement('label');
    nameLabel.textContent = this.translate.instant('Polygon_name_tr');
    nameLabel.style.display = 'block';
    nameLabel.style.fontSize = '12px';
    nameLabel.style.marginBottom = '4px';

    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.value = String((active.feature.properties as any)?.name ?? '').trim();
    nameInput.placeholder = this.translate.instant('Polygon_name_tr');
    nameInput.maxLength = 120;
    nameInput.style.width = '100%';
    nameInput.style.boxSizing = 'border-box';
    nameInput.style.padding = '6px';
    nameInput.style.marginBottom = '8px';
    wrap.appendChild(nameLabel);
    wrap.appendChild(nameInput);

    const wrap_t = document.createElement('div');
    wrap_t.className = 'coords-wrap_t';
    wrap_t.style.maxHeight = '250px';
    wrap_t.style.overflowY = 'auto';

    const table = document.createElement('table');
    table.className = 'coords-table';
    table.style.width = '100%';
    table.style.borderCollapse = 'collapse';

    const thead = document.createElement('thead');
    thead.innerHTML = `
      <tr>
        <th style="text-align:left; padding:4px 6px;">#</th>
        <th style="text-align:left; padding:4px 6px;">Latitude</th>
        <th style="text-align:left; padding:4px 6px;">Longitude</th>
        <th style="width:1%;"></th>
      </tr>`;
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    table.appendChild(tbody);

    const btnRow = document.createElement('div');
    btnRow.style.display = 'flex';
    btnRow.style.gap = '8px';
    btnRow.style.marginTop = '8px';

    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.textContent = 'Add row';
    addBtn.className = 'coords-popup__btn';
    const saveBtn = document.createElement('button');
    saveBtn.type = 'button';
    saveBtn.textContent = 'Save';
    saveBtn.className = 'coords-popup__btn';
    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.textContent = 'Cancel';
    cancelBtn.className = 'coords-popup__btn';

    btnRow.appendChild(addBtn);
    btnRow.appendChild(saveBtn);
    btnRow.appendChild(cancelBtn);

    wrap_t.appendChild(table);
    wrap.appendChild(wrap_t);
    wrap.appendChild(btnRow);

    const makeNumberInput = (val: number, placeholder: string) => {
      const inp = document.createElement('input');
      inp.type = 'number';
      inp.step = '0.000001';
      inp.placeholder = placeholder;
      inp.value = Number.isFinite(val) ? String(val) : '';
      inp.style.width = '100%';
      inp.style.boxSizing = 'border-box';
      inp.style.padding = '4px 6px';
      return inp;
    };

    const rebuildRowIndices = () => {
      Array.from(tbody.querySelectorAll('tr')).forEach((tr, idx) => {
        const first = tr.querySelector('.idx') as HTMLSpanElement;
        if (first) first.textContent = String(idx + 1);
      });
    };

    const addRow = (lat = openRing.at(-1)?.[1] ?? 0, lng = openRing.at(-1)?.[0] ?? 0) => {
      if (tbody.querySelectorAll('tr').length >= 20) {
        this.showErr('Unable_to_add_more_maximum_20_data_points_tr');
        return;
      }
      const tr = document.createElement('tr');
      tr.style.borderTop = '1px solid #e5e5e5';

      const tdIdx = document.createElement('td');
      tdIdx.style.padding = '4px 6px';
      const idxSpan = document.createElement('span');
      idxSpan.className = 'idx';
      tdIdx.appendChild(idxSpan);

      const tdLat = document.createElement('td');
      tdLat.style.padding = '4px 6px';
      const inpLat = makeNumberInput(lat, 'Lat');
      tdLat.appendChild(inpLat);

      const tdLng = document.createElement('td');
      tdLng.style.padding = '4px 6px';
      const inpLng = makeNumberInput(lng, 'Lng');
      tdLng.appendChild(inpLng);

      const tdDel = document.createElement('td');
      tdDel.style.padding = '4px 6px';
      const delBtn = document.createElement('button');
      delBtn.type = 'button';
      delBtn.textContent = '✕';
      delBtn.title = 'Delete row';
      delBtn.style.minWidth = '32px';
      delBtn.addEventListener('click', () => {
        tr.remove();
        rebuildRowIndices();
      });
      tdDel.appendChild(delBtn);

      tr.appendChild(tdIdx);
      tr.appendChild(tdLat);
      tr.appendChild(tdLng);
      tr.appendChild(tdDel);
      tbody.appendChild(tr);

      rebuildRowIndices();
    };

    if (openRing.length === 0) {
      addRow(0, 0);
      addRow(0, 0);
      addRow(0, 0);
    } else {
      for (const [lng, lat] of openRing) addRow(lat, lng);
    }

    addBtn.addEventListener('click', () => addRow());
    cancelBtn.addEventListener('click', () => {
      try {
        this.coordsPopup?.remove();
      } finally {
        this.coordsPopup = null;
      }
    });

    saveBtn.addEventListener('click', () => {
      const rows = Array.from(tbody.querySelectorAll('tr'));
      const newOpen: [number, number][] = [];
      for (const tr of rows) {
        const inputs = tr.querySelectorAll('input');
        const lat = parseFloat((inputs[0] as HTMLInputElement).value);
        const lng = parseFloat((inputs[1] as HTMLInputElement).value);
        if (!Number.isFinite(lat) || !Number.isFinite(lng))
          return this.showErr('All_rows_must_have_valid_numeric_Latitude_and_Longitude_tr');
        if (Math.abs(lat) > 90 || Math.abs(lng) > 180)
          return this.showErr('Lat_must_be_between_90_and_Lng_between_180_tr');
        newOpen.push([lng, lat]);
      }
      if (newOpen.length < 3) return this.showErr('A_polygon_needs_at_least_3_vertices_tr');

      const nextId = this.replacePolygon(active.id, newOpen);
      if (nextId) {
        const name = nameInput.value.trim();
        try {
          (this.draw as any).setFeatureProperty?.(nextId, 'name', name);
        } catch {}
        this.showMsg('Coordinates_updated_tr');
        this.refreshCtrl();
        this.coordsPopup?.remove();
        this.coordsPopup = null;
      }
    });

    try {
      this.coordsPopup = new mapboxgl.Popup({ closeButton: true, closeOnClick: false, offset: 12, maxWidth: '450px' })
        .setLngLat(at)
        .setDOMContent(wrap)
        .addTo(this.map);
    } catch (err) {
      this.showErr('Could_not_open_coordinates_editor_tr', err);
    }
  }

  ngOnDestroy(): void {
    this.stopTracesPendingWatch?.();
    try {
      if (this.animId) cancelAnimationFrame(this.animId);
    } catch {}
    try {
      if (this.map) {
        const canvas = this.map.getCanvas() as HTMLCanvasElement;
        canvas?.removeEventListener('keydown', this.onCanvasKeydown);
        this.map.off('draw.create', this.onDrawCreate);
        this.map.off('draw.update', this.onDrawUpdate);
        this.map.off('draw.delete', this.onDrawDelete);
        this.map.off('draw.modechange', this.onModeChange);
        this.map.off('draw.selectionchange', this.onSelectionChange);
        // NEW: remove mousedown hook
        this.map.off('mousedown', this.onMouseDownForceDirectSelect);
        // NEW: ensure click handler is off if circle-adding was active
        this.map.off('click', this.onMapClickAddCircle as any);
        this.searchControls?.remove();
        this.searchControls = null;
        if (this.toolsCtrl) this.map.removeControl(this.toolsCtrl);
        this.drawValidation.detach();
        this.map.remove();
      }
    } catch (err) {
      this.showErr('Map_cleanup_failed_tr', err);
    }
  }
}

// ---------- Controls (Polygons list; draw button + NEW circle button) ----------
const ICONS: Record<string, string> = {
  draw: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <g id="Edit / Add_To_Queue"> <path id="Vector" d="M3 9V19.4C3 19.9601 3 20.2399 3.10899 20.4538C3.20487 20.642 3.35774 20.7952 3.5459 20.8911C3.7596 21 4.0395 21 4.59846 21H15.0001M14 13V10M14 10V7M14 10H11M14 10H17M7 13.8002V6.2002C7 5.08009 7 4.51962 7.21799 4.0918C7.40973 3.71547 7.71547 3.40973 8.0918 3.21799C8.51962 3 9.08009 3 10.2002 3H17.8002C18.9203 3 19.4801 3 19.9079 3.21799C20.2842 3.40973 20.5905 3.71547 20.7822 4.0918C21.0002 4.51962 21.0002 5.07969 21.0002 6.19978L21.0002 13.7998C21.0002 14.9199 21.0002 15.48 20.7822 15.9078C20.5905 16.2841 20.2842 16.5905 19.9079 16.7822C19.4805 17 18.9215 17 17.8036 17H10.1969C9.07899 17 8.5192 17 8.0918 16.7822C7.71547 16.5905 7.40973 16.2842 7.21799 15.9079C7 15.4801 7 14.9203 7 13.8002Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g> </g></svg>`,
  circle: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="7" stroke="#000" stroke-width="2"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke="#000" stroke-width="1.5" stroke-linecap="round"/></svg>`,
  coords: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M12 20H20.5M18 10L21 7L17 3L14 6M18 10L8 20H4V16L14 6M18 10L14 6" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>`,
  delete: `<svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M36.3291 10.2931L29.0251 17.5971 25.4403 21.1822 26.8539 22.5958 37.7428 11.7069C38.1342 11.3156 38.1342 10.6834 37.7419 10.2922 37.3527 9.90193 36.7192 9.90193 36.3291 10.2931ZM28.2682 24.01L39.1571 13.1211C40.3297 11.9484 40.3297 10.0486 39.1562 8.87798 37.9872 7.70606 36.0847 7.70606 34.9139 8.87979L27.6108 16.1829 24.0261 19.768 23.3883 19.1301 13.1527 8.89569C11.9828 7.72286 10.0803 7.72286 8.90954 8.89659 7.74007 10.0691 7.74007 11.9675 8.91044 13.1379L19.7833 24.0108 8.92904 34.8651C8.46649 35.4607 7.76653 37.9264 8.92154 39.0798 10.0972 40.2298 11.4174 40.2314 12.5531 39.5674 13.1621 39.1163 21.9373 30.3413 21.9373 30.3413 22.3278 29.9508 22.3278 29.3176 21.9373 28.9271 21.5467 28.5366 20.9136 28.5366 20.523 28.9271L11.757 37.6931C11.1166 38.0905 10.7697 38.0901 10.351 37.6807 9.91539 37.2458 9.91609 36.921 10.1697 36.5091L24.0261 22.5964 25.4397 24.01 23.4948 25.9549C23.0117 26.7946 23.0645 27.2928 23.392 27.6203L34.908 39.1363C36.0806 40.3088 37.9797 40.3088 39.1523 39.1363 40.3226 37.9629 40.3226 36.0645 39.1523 34.8941L28.2682 24.01Z" fill="#bd0000"/></svg>`,
};

class MapToolsControl implements mapboxgl.IControl {
  private _map: mapboxgl.Map | null = null;
  private _container!: HTMLElement;

  private btnDraw!: HTMLButtonElement;
  private btnCircle!: HTMLButtonElement;
  private polyListWrap!: HTMLElement;
  private polyList!: HTMLElement;
  private rowById = new Map<string, HTMLElement>();

  constructor(
    private api: {
      startPolygon: () => void;
      startCircle: () => void;
      clearPolygon: () => void;
      deletePolygonById: (id: string) => void;
      getState: () => { isDrawing: boolean; isEditing: boolean; hasPolygon: boolean };

      getPolygonList: () => Array<{ id: string; label: string; selected: boolean; hasError: boolean }>;
      focusZoomEdit: (id: string) => void;
      focusZoomEditAndShowCoords: (id: string) => void;
    }
  ) {}

  private svg(name: string): string {
    return ICONS[name] ?? `<svg viewBox="0 0 24 24" width="20" height="20"><circle cx="12" cy="12" r="10"/></svg>`;
  }

  private iconBtn({
    svgName,
    title,
    onClick,
    small = false,
    aria = title,
  }: {
    svgName: string;
    title: string;
    onClick: () => void;
    small?: boolean;
    aria?: string;
  }) {
    const b = document.createElement('button');
    b.type = 'button';
    b.title = title;
    if (title === 'Start drawing a polygon') b.dataset['testid'] = 'map-start-polygon-button';
    if (title === 'Add circle from point') b.dataset['testid'] = 'map-add-circle-button';
    b.setAttribute('aria-label', aria);
    b.className = 'custom-tools-ctrl__btn' + (small ? ' custom-tools-ctrl__btn--sm' : '');
    b.innerHTML = this.svg(svgName);
    b.addEventListener('click', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      onClick();
    });
    return b;
  }

  onAdd(map: mapboxgl.Map) {
    this._map = map;

    const root = document.createElement('div');
    root.className = 'mapboxgl-ctrl mapboxgl-ctrl-group custom-tools-ctrl';
    root.style.maxWidth = '350px';
    root.style.minWidth = '150px';
    root.style.display = 'flex';
    root.style.flexDirection = 'column';
    root.style.maxHeight = '93vh';

    // Header row: "Polygons" (left) + Circle + Draw (right)
    const headerRow = document.createElement('div');
    headerRow.style.display = 'flex';
    headerRow.style.alignItems = 'center';
    headerRow.style.justifyContent = 'space-between';
    headerRow.style.padding = '6px';
    headerRow.style.borderRadius = '5px';
    headerRow.style.background = '#fff';
    headerRow.style.borderBottom = '1px solid #e5e5e5';

    const listHeader = document.createElement('div');
    listHeader.textContent = 'Polygons';
    listHeader.style.fontWeight = '600';
    listHeader.style.fontSize = '12px';

    this.btnCircle = this.iconBtn({
      svgName: 'circle',
      title: 'Add circle from point',
      onClick: () => this.api.startCircle(),
    });

    this.btnDraw = this.iconBtn({
      svgName: 'draw',
      title: 'Start drawing a polygon',
      onClick: () => this.api.startPolygon(),
    });

    const rightBtns = document.createElement('div');
    rightBtns.style.display = 'flex';
    rightBtns.style.gap = '4px';
    rightBtns.appendChild(this.btnCircle); // left side
    rightBtns.appendChild(this.btnDraw); // right side

    headerRow.appendChild(listHeader);
    headerRow.appendChild(rightBtns);

    // Polygon list
    const listWrap = document.createElement('div');
    listWrap.style.overflow = 'auto';
    listWrap.style.borderRadius = '6px';
    listWrap.style.display = 'none';
    listWrap.style.background = '#fff';
    listWrap.style.maxHeight = '240px';

    const ul = document.createElement('div');
    ul.style.display = 'flex';
    ul.style.flexDirection = 'column';
    ul.style.padding = '4px';
    ul.style.gap = '4px';

    listWrap.appendChild(ul);

    root.appendChild(headerRow);
    root.appendChild(listWrap);

    this.polyListWrap = listWrap;
    this.polyList = ul;

    this._container = root;
    this.refresh();
    return this._container;
  }

  // public: make list height follow map canvas height
  syncListHeightToMap(map: mapboxgl.Map) {
    try {
      const mapH = (map.getContainer() as HTMLElement).clientHeight;
      const headerH = (this._container.children[0] as HTMLElement)?.offsetHeight ?? 0;
      const extra = 16;
      const max = Math.max(120, mapH - headerH - extra);
      this.polyListWrap.style.maxHeight = `${max}px`;
    } catch {}
  }

  // public: scroll to a polygon row
  scrollToPolygon(id: string) {
    const row = this.rowById.get(id);
    if (!row) return;
    try {
      row.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    } catch {}
  }

  private renderPolygonList() {
    const list = this.api.getPolygonList();
    const s = this.api.getState();

    // Hide list while drawing; show only when not drawing
    this.polyListWrap.style.display = !s.isDrawing && list.length ? 'block' : 'none';
    this.polyList.replaceChildren();
    this.rowById.clear();

    for (const item of list) {
      const row = document.createElement('div');
      row.style.display = 'flex';
      row.style.alignItems = 'center';
      row.style.justifyContent = 'space-between';
      row.style.paddingLeft = '4px';
      row.style.borderRadius = '4px';
      row.style.cursor = 'pointer';
      row.style.userSelect = 'none';
      row.style.border = '1px solid transparent';

      if (item.selected) {
        row.style.background = '#e8f5e9';
        row.style.borderColor = '#a5d6a7';
        if (item.hasError) row.style.boxShadow = 'inset 0 0 0 1px #f1998e';
        setTimeout(() => this.scrollToPolygon(item.id), 0);
      } else if (item.hasError) {
        row.style.background = '#fdecea';
        row.style.borderColor = '#f5c2c7';
      } else {
        row.addEventListener('mouseenter', () => (row.style.background = '#f7f7f7'));
        row.addEventListener('mouseleave', () => (row.style.background = 'transparent'));
      }

      const label = document.createElement('div');
      label.textContent = item.label;
      label.style.fontSize = '11px';
      label.style.flex = '1 1 auto';

      const actions = document.createElement('div');
      actions.style.display = 'flex';
      actions.style.flex = '0 0 auto';

      // Coords icon: zoom + edit + coords
      const coordsBtn = this.iconBtn({
        svgName: 'coords',
        title: 'Show / edit coords (zoom & edit)',
        small: true,
        onClick: () => this.api.focusZoomEditAndShowCoords(item.id),
      });
      coordsBtn.style.minWidth = '24px';

      // Delete icon — direct delete, no focus/zoom
      const delBtn = this.iconBtn({
        svgName: 'delete',
        title: 'Delete polygon',
        small: true,
        onClick: () => this.api.deletePolygonById(item.id),
      });
      delBtn.style.minWidth = '24px';

      row.appendChild(label);
      actions.appendChild(coordsBtn);
      actions.appendChild(delBtn);
      row.appendChild(actions);

      // Clicking the row: zoom + edit (no coords)
      row.addEventListener('click', (ev) => {
        ev.preventDefault();
        this.api.focusZoomEdit(item.id);
      });

      this.polyList.appendChild(row);
      this.rowById.set(item.id, row);
    }
  }

  onRemove() {
    this._container?.parentNode?.removeChild(this._container);
    this._map = null;
  }

  refresh() {
    if (!this._container) return;
    this.renderPolygonList();
  }
}

/**
 * "amber = rejected by TRACES · violet = precision-repaired by TRACES" — plain DOM, no `symbol`
 * layer, so it does not interact with the e2e Mapbox stub at all. `data-testid` attributes are
 * the e2e hook (Task 10): `map-legend-traces`, `map-legend-traces-stale`, `map-legend-precision`.
 */
class MapLegendControl implements mapboxgl.IControl {
  private _container!: HTMLElement;

  constructor(
    private opts: {
      traces: boolean;
      precision: boolean;
      shape: boolean;
      stale: boolean;
      translate: (key: string) => string;
    }
  ) {}

  onAdd(): HTMLElement {
    const root = document.createElement('div');
    root.className = 'mapboxgl-ctrl mapboxgl-ctrl-group traces-legend-ctrl';
    root.setAttribute('data-testid', 'map-legend');
    root.style.background = '#fff';
    root.style.padding = '6px 10px';
    root.style.fontSize = '11px';
    root.style.display = 'flex';
    root.style.flexDirection = 'column';
    root.style.gap = '4px';

    const swatchRow = (testId: string, color: string, text: string) => {
      const row = document.createElement('div');
      row.setAttribute('data-testid', testId);
      const dot = document.createElement('span');
      dot.style.display = 'inline-block';
      dot.style.width = '10px';
      dot.style.height = '10px';
      dot.style.borderRadius = '50%';
      dot.style.background = color;
      dot.style.marginRight = '6px';
      row.appendChild(dot);
      row.appendChild(document.createTextNode(text));
      return row;
    };

    if (this.opts.traces) {
      root.appendChild(swatchRow('map-legend-traces', TRACES_AMBER, this.opts.translate('traces_rejection_legend_tr')));
      if (this.opts.stale) {
        const staleRow = document.createElement('div');
        staleRow.setAttribute('data-testid', 'map-legend-traces-stale');
        staleRow.textContent = this.opts.translate('traces_rejection_stale_tr');
        root.appendChild(staleRow);
      }
    }
    if (this.opts.precision) {
      root.appendChild(
        swatchRow('map-legend-precision', PRECISION_VIOLET, this.opts.translate('traces_rejection_legend_precision_tr'))
      );
    }
    if (this.opts.shape) {
      root.appendChild(
        swatchRow('map-legend-shape', PRECISION_VIOLET, this.opts.translate('traces_rejection_legend_shape_tr'))
      );
    }

    this._container = root;
    return root;
  }

  onRemove(): void {
    this._container?.remove();
  }

  getDefaultPosition(): mapboxgl.ControlPosition {
    return 'bottom-left';
  }
}
